import { useState, useEffect, useRef } from "react";
import { createClient } from "@supabase/supabase-js";

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
);

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&family=Inter:wght@300;400;500;600&display=swap');

  * { margin: 0; padding: 0; box-sizing: border-box; }

  :root {
    --bg: #fafaf8;
    --surface: #ffffff;
    --surface2: #f4f4f0;
    --border: #e8e8e2;
    --accent: #1a5c3a;
    --accent-light: #e8f2ec;
    --accent-hover: #144d30;
    --text: #1a1a18;
    --muted: #6b6b60;
    --subtle: #9a9a8e;
    --card: #f9f9f6;
  }

  html, body, #root { height: 100%; background: var(--bg); color: var(--text); font-family: 'Inter', sans-serif; }
  .app { height: 100vh; overflow: hidden; display: flex; flex-direction: column; }

  /* ── LANDING ── */
  .landing {
    flex: 1; display: flex; overflow: hidden;
  }

  .landing-left {
    flex: 1; display: flex; flex-direction: column; justify-content: center;
    padding: 5rem 6rem; background: var(--bg);
    border-right: 1px solid var(--border);
  }

  .landing-right {
    width: 440px; flex-shrink: 0;
    display: flex; flex-direction: column; justify-content: center;
    padding: 4rem 3.5rem; background: var(--surface);
  }

  .logo {
    font-family: 'Playfair Display', serif;
    font-size: 1.1rem; font-weight: 600; letter-spacing: 0.05em;
    color: var(--accent); margin-bottom: 4rem;
  }

  .landing-headline {
    font-family: 'Playfair Display', serif;
    font-size: clamp(2.4rem, 4vw, 3.8rem); font-weight: 500;
    line-height: 1.15; color: var(--text); margin-bottom: 1.5rem;
    max-width: 560px;
  }

  .landing-headline em { font-style: italic; color: var(--accent); }

  .landing-sub {
    font-size: 1rem; color: var(--muted); line-height: 1.75;
    max-width: 480px; margin-bottom: 3rem; font-weight: 300;
  }

  .stats-row {
    display: flex; gap: 3rem;
  }

  .stat-item {}
  .stat-number {
    font-family: 'Playfair Display', serif;
    font-size: 1.8rem; font-weight: 600; color: var(--text);
    line-height: 1;
  }
  .stat-label { font-size: 0.78rem; color: var(--subtle); margin-top: 0.3rem; letter-spacing: 0.03em; text-transform: uppercase; }

  /* ── FORM ── */
  .form-heading {
    font-family: 'Playfair Display', serif;
    font-size: 1.5rem; font-weight: 500; margin-bottom: 0.5rem; color: var(--text);
  }
  .form-sub { font-size: 0.875rem; color: var(--muted); margin-bottom: 2.5rem; line-height: 1.6; }

  .btn-google {
    width: 100%; padding: 0.9rem 1.25rem;
    background: var(--text); color: #fff;
    border: none; border-radius: 8px; cursor: pointer;
    font-family: 'Inter', sans-serif; font-size: 0.9rem; font-weight: 500;
    display: flex; align-items: center; justify-content: center; gap: 0.75rem;
    transition: background 0.2s, transform 0.15s;
    letter-spacing: 0.01em;
  }
  .btn-google:hover { background: #2a2a28; transform: translateY(-1px); }
  .btn-google svg { width: 18px; height: 18px; flex-shrink: 0; }

  .divider { display: flex; align-items: center; gap: 1rem; margin: 1.5rem 0; }
  .divider-line { flex: 1; height: 1px; background: var(--border); }
  .divider-text { font-size: 0.75rem; color: var(--subtle); }

  .login-note {
    font-size: 0.78rem; color: var(--subtle); line-height: 1.6;
    text-align: center; margin-top: 1.5rem;
    padding-top: 1.5rem; border-top: 1px solid var(--border);
  }

  /* ── MOBILE LANDING ── */
  @media (max-width: 768px) {
    .landing { flex-direction: column; overflow-y: auto; }
    .landing-left { padding: 3rem 2rem 2rem; border-right: none; border-bottom: 1px solid var(--border); }
    .landing-right { width: 100%; padding: 2.5rem 2rem; }
    .stats-row { gap: 2rem; }
  }

  /* ── LOADING ── */
  .loading-screen { flex: 1; display: flex; align-items: center; justify-content: center; flex-direction: column; gap: 1rem; background: var(--bg); }
  .loading-spinner { width: 28px; height: 28px; border: 2px solid var(--border); border-top-color: var(--accent); border-radius: 50%; animation: spin 0.8s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }
  .loading-text { color: var(--muted); font-size: 0.875rem; }

  /* ── CHAT LAYOUT ── */
  .chat-layout { flex: 1; display: flex; flex-direction: column; overflow: hidden; background: var(--bg); }

  .chat-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 0 2rem; height: 60px;
    border-bottom: 1px solid var(--border);
    background: var(--surface); flex-shrink: 0;
  }

  .header-logo {
    font-family: 'Playfair Display', serif;
    font-size: 1.1rem; font-weight: 600; color: var(--accent);
    letter-spacing: 0.05em;
  }

  .header-right { display: flex; align-items: center; gap: 1rem; }

  .header-user-name { font-size: 0.85rem; color: var(--muted); }

  .avatar {
    width: 30px; height: 30px; border-radius: 50%;
    background: var(--accent-light); border: 1px solid var(--border);
    display: flex; align-items: center; justify-content: center;
    font-size: 0.7rem; font-weight: 600; color: var(--accent);
    overflow: hidden; flex-shrink: 0;
  }
  .avatar img { width: 100%; height: 100%; object-fit: cover; }

  .btn-logout {
    background: none; border: 1px solid var(--border); border-radius: 6px;
    color: var(--muted); padding: 0.3rem 0.75rem; font-size: 0.78rem;
    cursor: pointer; transition: all 0.15s; font-family: 'Inter', sans-serif;
  }
  .btn-logout:hover { border-color: var(--text); color: var(--text); }

  /* ── MESSAGES ── */
  .chat-messages {
    flex: 1; overflow-y: auto; padding: 2.5rem 2rem;
    display: flex; flex-direction: column; gap: 1.75rem;
    max-width: 900px; width: 100%; margin: 0 auto; align-self: stretch;
  }
  .chat-messages::-webkit-scrollbar { width: 4px; }
  .chat-messages::-webkit-scrollbar-thumb { background: var(--border); border-radius: 2px; }

  .message-row { display: flex; gap: 1rem; animation: msgIn 0.25s ease forwards; }
  .message-row.user { flex-direction: row-reverse; }
  .message-row.assistant { flex-direction: row; }

  @keyframes msgIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }

  .msg-avatar {
    width: 32px; height: 32px; border-radius: 50%; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    font-size: 0.7rem; font-weight: 600; overflow: hidden;
  }
  .msg-avatar.ai {
    background: var(--accent); color: white;
    font-family: 'Playfair Display', serif; font-size: 0.8rem;
  }
  .msg-avatar.user-av {
    background: var(--surface2); border: 1px solid var(--border);
    color: var(--muted);
  }
  .msg-avatar.user-av img { width: 100%; height: 100%; object-fit: cover; border-radius: 50%; }

  .message-bubble {
    padding: 0.875rem 1.125rem; border-radius: 12px;
    max-width: 680px; font-size: 0.925rem; line-height: 1.75;
  }

  .message-row.assistant .message-bubble {
    background: var(--surface); border: 1px solid var(--border);
    border-radius: 2px 12px 12px 12px; color: var(--text);
  }

  .message-row.user .message-bubble {
    background: var(--accent); color: white;
    border-radius: 12px 2px 12px 12px;
  }

  .message-bubble p { margin-bottom: 0.6rem; }
  .message-bubble p:last-child { margin-bottom: 0; }
  .message-bubble strong { font-weight: 600; }
  .message-row.assistant .message-bubble strong { color: var(--accent); }
  .message-bubble h1, .message-bubble h2, .message-bubble h3 {
    font-family: 'Playfair Display', serif; margin: 1rem 0 0.5rem; font-weight: 500;
  }
  .message-bubble ul, .message-bubble ol { padding-left: 1.5rem; margin-bottom: 0.6rem; }
  .message-bubble li { margin-bottom: 0.25rem; }
  .message-bubble code { background: var(--surface2); padding: 0.1rem 0.4rem; border-radius: 3px; font-size: 0.85rem; }
  .message-bubble hr { border: none; border-top: 1px solid var(--border); margin: 1rem 0; }

  /* ── TYPING ── */
  .typing-indicator { display: flex; gap: 4px; align-items: center; padding: 0.25rem 0; }
  .typing-dot { width: 6px; height: 6px; background: var(--border); border-radius: 50%; animation: typingPulse 1.4s ease infinite; }
  .typing-dot:nth-child(2) { animation-delay: 0.2s; }
  .typing-dot:nth-child(3) { animation-delay: 0.4s; }
  @keyframes typingPulse { 0%, 60%, 100% { opacity: 0.4; transform: scale(0.9); } 30% { opacity: 1; transform: scale(1.1); background: var(--accent); } }

  /* ── SUGGESTIONS ── */
  .suggestions-wrap {
    max-width: 900px; width: 100%; margin: 0 auto; align-self: stretch;
    padding: 0 2rem 1rem; display: flex; gap: 0.5rem; flex-wrap: wrap;
  }
  .suggestion-chip {
    padding: 0.4rem 1rem; background: var(--surface);
    border: 1px solid var(--border); border-radius: 100px;
    font-size: 0.8rem; color: var(--muted); cursor: pointer;
    transition: all 0.15s; font-family: 'Inter', sans-serif;
    white-space: nowrap;
  }
  .suggestion-chip:hover { border-color: var(--accent); color: var(--accent); background: var(--accent-light); }

  /* ── INPUT ── */
  .chat-input-wrap {
    border-top: 1px solid var(--border); background: var(--surface);
    padding: 1rem 2rem 1.25rem; flex-shrink: 0;
  }
  .chat-input-inner {
    max-width: 900px; margin: 0 auto;
  }
  .input-row {
    display: flex; gap: 0.75rem; align-items: flex-end;
    background: var(--bg); border: 1px solid var(--border);
    border-radius: 10px; padding: 0.75rem 0.75rem 0.75rem 1.125rem;
    transition: border-color 0.2s, box-shadow 0.2s;
  }
  .input-row:focus-within { border-color: var(--accent); box-shadow: 0 0 0 3px rgba(26,92,58,0.08); }
  .chat-textarea {
    flex: 1; background: none; border: none; outline: none; color: var(--text);
    font-family: 'Inter', sans-serif; font-size: 0.925rem; line-height: 1.5;
    resize: none; min-height: 24px; max-height: 160px;
  }
  .chat-textarea::placeholder { color: var(--subtle); }
  .send-btn {
    width: 34px; height: 34px; border-radius: 7px;
    background: var(--accent); border: none; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; color: white; transition: background 0.15s, transform 0.1s;
  }
  .send-btn:hover { background: var(--accent-hover); transform: scale(1.04); }
  .send-btn:disabled { opacity: 0.35; cursor: not-allowed; transform: none; }
  .input-footer { display: flex; justify-content: space-between; padding: 0.5rem 0.25rem 0; }
  .input-hint { font-size: 0.7rem; color: var(--subtle); }

  /* ── WELCOME ── */
  .welcome-wrap {
    flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center;
    padding: 3rem 2rem; gap: 2rem; text-align: center;
    max-width: 900px; width: 100%; margin: 0 auto; align-self: stretch;
  }
  .welcome-logo {
    font-family: 'Playfair Display', serif;
    font-size: 2rem; font-weight: 500; color: var(--accent);
  }
  .welcome-heading {
    font-family: 'Playfair Display', serif;
    font-size: 1.6rem; font-weight: 500; color: var(--text); max-width: 500px; line-height: 1.35;
  }
  .welcome-sub { font-size: 0.925rem; color: var(--muted); max-width: 420px; line-height: 1.7; }

  .starter-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.75rem; max-width: 600px; width: 100%; }
  .starter-card {
    padding: 1.125rem 1.25rem; background: var(--surface);
    border: 1px solid var(--border); border-radius: 10px;
    cursor: pointer; text-align: left;
    transition: border-color 0.2s, box-shadow 0.2s;
  }
  .starter-card:hover { border-color: var(--accent); box-shadow: 0 2px 12px rgba(26,92,58,0.08); }
  .starter-label { font-size: 0.7rem; font-weight: 600; color: var(--accent); text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 0.4rem; }
  .starter-text { font-size: 0.85rem; color: var(--text); line-height: 1.5; font-weight: 400; }
`;

function renderMessage(text) {
  if (!text) return "";
  let html = text
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/\*([^*]+)\*/g, "<em>$1</em>")
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    .replace(/^### (.+)$/gm, "<h3>$1</h3>")
    .replace(/^## (.+)$/gm, "<h2>$1</h2>")
    .replace(/^# (.+)$/gm, "<h1>$1</h1>")
    .replace(/^---$/gm, "<hr/>")
    .replace(/^[-•] (.+)$/gm, "<li>$1</li>")
    .replace(/(<li>.*<\/li>\n?)+/g, m => "<ul>" + m + "</ul>")
    .replace(/\n\n/g, "</p><p>")
    .replace(/\n/g, "<br/>");
  return "<p>" + html + "</p>";
}

const SUGGESTIONS = [
  "Quero entender o tamanho do meu mercado na Florida Central",
  "Quero conhecer os potenciais concorrentes do meu negócio na Florida Central",
  "Quero entender possíveis sinergias de usar parte do meu time remotamente no Brasil",
  "Quero expandir minha empresa para a Florida Central, qual o passo a passo?",
];

const STARTERS = [
  { label: "Tamanho de Mercado", text: "Quero entender o tamanho do meu mercado na Florida Central" },
  { label: "Análise de Concorrência", text: "Quero conhecer os potenciais concorrentes do meu negócio na Florida Central" },
  { label: "Sinergias Brasil–EUA", text: "Quero entender possíveis sinergias de usar parte do meu time remotamente no Brasil em uma operação na Florida Central" },
  { label: "Passo a Passo", text: "Quero expandir minha empresa para a Florida Central, qual o passo a passo?" },
];

export default function OfliApp() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setAuthLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      setAuthLoading(false);
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (user && !historyLoaded) loadHistory();
  }, [user]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const loadHistory = async () => {
    const { data, error } = await supabase
      .from("messages")
      .select("role, content")
      .eq("user_id", user.id)
      .order("created_at", { ascending: true });
    if (!error && data) {
      setMessages(data);
      if (data.length > 0) setShowSuggestions(true);
    }
    setHistoryLoaded(true);
  };

  const saveMessage = async (role, content) => {
    await supabase.from("messages").insert({ user_id: user.id, role, content });
  };

  const handleGoogleLogin = async () => {
    await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: window.location.origin },
    });
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setMessages([]);
    setHistoryLoaded(false);
    setShowSuggestions(false);
  };

  const sendMessage = async (text) => {
    const msg = text || input.trim();
    if (!msg || loading) return;
    setInput("");
    setShowSuggestions(false);
    const newMessages = [...messages, { role: "user", content: msg }];
    setMessages(newMessages);
    setLoading(true);
    await saveMessage("user", msg);
    try {
      const res = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: newMessages,
          userName: user?.user_metadata?.full_name || user?.email || "Empreendedor",
        }),
      });
      const data = await res.json();
      const reply = data.reply || "Desculpe, houve um erro. Tente novamente.";
      setMessages([...newMessages, { role: "assistant", content: reply }]);
      await saveMessage("assistant", reply);
      setShowSuggestions(true);
    } catch (e) {
      setMessages([...newMessages, { role: "assistant", content: "Ops! Houve um erro de conexão. Por favor, tente novamente." }]);
    }
    setLoading(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const handleTextareaInput = (e) => {
    setInput(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 160) + "px";
  };

  const userName = user?.user_metadata?.full_name || user?.email || "";
  const userAvatar = user?.user_metadata?.avatar_url || null;
  const initials = userName.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase() || "OF";

  if (authLoading) return (
    <><style>{styles}</style>
    <div className="app"><div className="loading-screen">
      <div className="loading-spinner" /><div className="loading-text">Carregando...</div>
    </div></div></>
  );

  return (
    <><style>{styles}</style>
    <div className="app">

      {/* ── LANDING ── */}
      {!user && (
        <div className="landing">
          <div className="landing-left">
            <div className="logo">Ofli</div>
            <h1 className="landing-headline">
              Expanda seu negócio para a <em>Florida Central</em>
            </h1>
            <p className="landing-sub">
              Mentoria especializada em expandir negócios de brasileiros para a Florida Central. Análise de mercado, concorrência e oportunidades em tempo real.
            </p>

          </div>
          <div className="landing-right">
            <div className="form-heading">Inicie sua análise</div>
            <p className="form-sub">Acesse com sua conta Google e comece a explorar oportunidades na Florida Central.</p>
            <button className="btn-google" onClick={handleGoogleLogin}>
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Continuar com Google
            </button>
            <p className="login-note">Seu histórico de conversas fica salvo. Volte quando quiser e continue de onde parou.</p>
          </div>
        </div>
      )}

      {/* ── CHAT ── */}
      {user && (
        <div className="chat-layout">
          <header className="chat-header">
            <div className="header-logo">Ofli</div>
            <div className="header-right">
              <span className="header-user-name">{userName.split(" ")[0]}</span>
              <div className="avatar">{userAvatar ? <img src={userAvatar} alt={userName} /> : initials}</div>
              <button className="btn-logout" onClick={handleLogout}>Sair</button>
            </div>
          </header>

          <div className="chat-messages">
            {messages.length === 0 && historyLoaded ? (
              <div className="welcome-wrap">
                <div className="welcome-logo">Ofli</div>
                <div className="welcome-heading">Olá, {userName.split(" ")[0]}. Como posso ajudar hoje?</div>

                <div className="starter-grid">
                  {STARTERS.map((s, i) => (
                    <div className="starter-card" key={i} onClick={() => sendMessage(s.text)}>
                      <div className="starter-label">{s.label}</div>
                      <div className="starter-text">{s.text}</div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((m, i) => (
                <div key={i} className={`message-row ${m.role}`}>
                  {m.role === "assistant"
                    ? <div className="msg-avatar ai">O</div>
                    : <div className="msg-avatar user-av">{userAvatar ? <img src={userAvatar} alt={initials} /> : initials}</div>
                  }
                  <div className="message-bubble" dangerouslySetInnerHTML={{__html: renderMessage(m.content)}} />
                </div>
              ))
            )}
            {loading && (
              <div className="message-row assistant">
                <div className="msg-avatar ai">O</div>
                <div className="message-bubble">
                  <div className="typing-indicator"><div className="typing-dot" /><div className="typing-dot" /><div className="typing-dot" /></div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {showSuggestions && messages.length > 0 && !loading && (
            <div className="suggestions-wrap">
              {SUGGESTIONS.map((s, i) => <div key={i} className="suggestion-chip" onClick={() => sendMessage(s)}>{s}</div>)}
            </div>
          )}

          <div className="chat-input-wrap">
            <div className="chat-input-inner">
              <div className="input-row">
                <textarea ref={textareaRef} className="chat-textarea" placeholder="Descreva seu negócio ou faça uma pergunta..." value={input} onChange={handleTextareaInput} onKeyDown={handleKeyDown} rows={1} />
                <button className="send-btn" onClick={() => sendMessage()} disabled={!input.trim() || loading}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
                  </svg>
                </button>
              </div>
              <div className="input-footer">
                <span className="input-hint">Enter para enviar · Shift+Enter para nova linha</span>
                <span className="input-hint">powered by Claude</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div></>
  );
}

